import type { IEntry } from '@app/domain/instrument/instrument.interface'
import type {
  IVisit,
  IVisitDefinition,
  IVisitState,
} from '@app/domain/visit/visit.interface'
import type { AppAction } from '@app/state'

import type { IApiUpdateVisit } from './helpers'

export const enum ActionStatusId {
  Pending = 'pending',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

export interface State {
  creationState?: ActionStatusId
  currentVisit?: {
    visit?: IVisit
    definition?: IVisitDefinition
    entries?: Record<string, IEntry>
  }
  definitions?: Array<IVisitDefinition>
  errorMessage?: string
  downloadReportErrorMessage?: string
  isInstrumentPdfLoading?: boolean
  isInstrumentModalOpen: boolean
  list: Array<IVisit>
  loadVisitStatus?: ActionStatusId
  visitAction?: {
    status: ActionStatusId
  }
}

export enum ActionType {
  AddInstrumentToVisit = 'VISITS/ADD_INSTRUMENT',
  AddInstrumentToVisitStarted = 'VISITS/ADD_INSTRUMENT/STARTED',
  AddInstrumentToVisitSucceeded = 'VISITS/ADD_INSTRUMENT/SUCCEEDED',
  AddInstrumentToVisitFailed = 'VISITS/ADD_INSTRUMENT/FAILED',

  AddInstrumentModalOpened = 'VISITS/ADD_INSTRUMENT_MODAL_OPENED',
  AddInstrumentModalClosed = 'VISITS/ADD_INSTRUMENT_MODAL_CLOSED',

  ClearActionStatus = 'VISITS/CLEAR_ACTION_STATUS',

  ClearDownloadError = 'VISITS/CLEAR_DOWNLOAD_ERROR',
  DownloadEntryPDF = 'VISITS/DOWNLOAD_ENTRY_PDF',
  DownloadEntryPDFSuccess = 'VISITS/DOWNLOAD_ENTRY_PDF/SUCCESS',
  DownloadEntryPDFFailed = 'VISITS/DOWNLOAD_ENTRY_PDF/FAILED',

  GetList = 'VISITS/GET_VISITS',
  VisitRetrieved = 'VISITS/VISIT_RETRIEVED',
  ListRetrieved = 'VISITS/LIST_RETRIEVED',

  LoadVisit = 'VISITS/LOAD_VISIT',
  LoadVisitStarted = 'VISITS/LOAD_VISIT/STARTED',
  LoadVisitSuccess = 'VISITS/LOAD_VISIT/SUCCESS',
  LoadVisitFailure = 'VISITS/LOAD_VISIT/FAILURE',

  GetDefinitionList = 'VISITS/GET_DEFINITION_LIST',
  GetDefinitionListStarted = 'VISITS/GET_DEFINITION_LIST/STARTED',
  GetDefinitionListSucceeded = 'VISITS/GET_DEFINITION_LIST/SUCCEEDED',
  GetDefinitionListFailed = 'VISITS/GET_DEFINITION_LIST/FAILED',

  GetVisitDefinition = 'VISITS/GET_VISIT_DEFINITION',
  GetVisitDefinitionStarted = 'VISITS/GET_VISIT_DEFINITION/STARTED',
  GetVisitDefinitionSucceeded = 'VISITS/GET_VISIT_DEFINITION/SUCCEEDED',
  GetVisitDefinitionFailed = 'VISITS/GET_VISIT_DEFINITION/FAILED',

  GetEntries = 'VISITS/GET_ENTRIES',
  EntriesRetrieved = 'VISITS/ENTRIES_RETRIEVED',

  GetVisitListForDate = 'VISITS/GET_VISIT_LIST_FOR_DATE',
  GetVisitListForDateStarted = 'VISITS/GET_VISIT_LIST_FOR_DATE/STARTED',
  GetVisitListForDateSuccess = 'VISITS/GET_VISIT_LIST_FOR_DATE/SUCCESS',
  GetVisitListForDateFailure = 'VISITS/GET_VISIT_LIST_FOR_DATE/FAILURE',

  ReloadVisit = 'VISITS/RELOAD',
  ReloadVisitBegin = 'VISITS/RELOAD/BEGIN',
  ReloadVisitSuccess = 'VISITS/RELOAD/SUCCESS',
  ReloadVisitFailure = 'VISITS/RELOAD/FAILURE',

  UpdateVisit = 'VISITS/UPDATE',
  UpdateVisitBegin = 'VISITS/UPDATE/BEGIN',
  UpdateVisitSuccess = 'VISITS/UPDATE/SUCCESS',
  UpdateVisitFailure = 'VISITS/UPDATE/FAILURE',

  UpdateVisitState = 'VISITS/UPDATE_STATE',
  UpdateVisitStateStarted = 'VISITS/UPDATE_STATE/STARTED',
  UpdateVisitStateSuccess = 'VISITS/UPDATE_STATE/SUCCESS',
  UpdateVisitStateFailure = 'VISITS/UPDATE_STATE/FAILURE',

  UpdateVisitStatus = 'VISITS/UPDATE_VISIT_STATUS',
  UpdateVisitStatusSuccess = 'VISITS/UPDATE_VISIT_STATUS/SUCCESS',
  UpdateVisitStatusFailure = 'VISITS/UPDATE_VISIT_STATUS/FAILURE',

  CreateVisit = 'VISITS/CREATE_VISIT',
  VisitCreationStarted = 'VISITS/CREATE_VISIT/STARTED',
  VisitCreationSucceeded = 'VISITS/CREATE_VISIT/SUCCEEDED',
  VisitCreationFailed = 'VISITS/CREATE_VISIT/FAILED',
}

export type Action =
  | {
      type: ActionType.AddInstrumentToVisit
      visitId: string
      instrumentId: string
    }
  | { type: ActionType.AddInstrumentToVisitStarted }
  | { type: ActionType.AddInstrumentToVisitFailed; error: string }
  | {
      type: ActionType.AddInstrumentToVisitSucceeded
      status: string
    }
  | { type: ActionType.AddInstrumentModalOpened }
  | { type: ActionType.AddInstrumentModalClosed }
  | { type: ActionType.ClearActionStatus }
  | { type: ActionType.GetList }
  | { type: ActionType.ListRetrieved; list: Array<IVisit> }
  | { type: ActionType.LoadVisit; visitId: string }
  | { type: ActionType.LoadVisitStarted }
  | { type: ActionType.LoadVisitSuccess }
  | { type: ActionType.LoadVisitFailure }
  | { type: ActionType.VisitRetrieved; visit: IVisit }
  | { type: ActionType.GetEntries }
  | { type: ActionType.EntriesRetrieved; entries: Array<IEntry> }
  | {
      type: ActionType.CreateVisit
      patientId: string
      definitionId: string
      startTime: string
      location: string
    }
  | { type: ActionType.GetVisitListForDate; date: string }
  | { type: ActionType.GetVisitListForDateStarted }
  | { type: ActionType.GetVisitListForDateSuccess; visits: Array<IVisit> }
  | { type: ActionType.GetVisitListForDateFailure; error: string }
  | { type: ActionType.VisitCreationStarted }
  | { type: ActionType.VisitCreationSucceeded; visit: IVisit }
  | { type: ActionType.VisitCreationFailed; error: string }
  | { type: ActionType.UpdateVisitStatus; visitId: string; status: string }
  | { type: ActionType.UpdateVisitStatusSuccess; visit: IVisit }
  | { type: ActionType.UpdateVisitStatusFailure; error: string }
  | { type: ActionType.UpdateVisit; id: string; visit: IApiUpdateVisit }
  | { type: ActionType.UpdateVisitBegin }
  | { type: ActionType.UpdateVisitSuccess; visit: IVisit }
  | { type: ActionType.UpdateVisitFailure; error: string }
  | { type: ActionType.UpdateVisitState; visitId: string; state: IVisitState }
  | { type: ActionType.UpdateVisitStateStarted }
  | { type: ActionType.UpdateVisitStateSuccess; visit: IVisit }
  | { type: ActionType.UpdateVisitStateFailure; error: string }
  | { type: ActionType.ReloadVisit; id: string }
  | { type: ActionType.ReloadVisitBegin }
  | { type: ActionType.ReloadVisitSuccess }
  | { type: ActionType.ReloadVisitFailure; error: string }
  | { type: ActionType.GetVisitDefinition; visitId: string }
  | { type: ActionType.GetVisitDefinitionStarted }
  | { type: ActionType.GetVisitDefinitionFailed; error: string }
  | {
      type: ActionType.GetVisitDefinitionSucceeded
      definition: IVisitDefinition
    }
  | { type: ActionType.GetDefinitionList }
  | { type: ActionType.GetDefinitionListStarted }
  | {
      type: ActionType.GetDefinitionListSucceeded
      definitions: Array<IVisitDefinition>
    }
  | { type: ActionType.GetDefinitionListFailed; error: string }
  | { type: ActionType.DownloadEntryPDF; entryId: string; templateId: string }
  | { type: ActionType.DownloadEntryPDFSuccess }
  | { type: ActionType.DownloadEntryPDFFailed; error: string }
  | { type: ActionType.ClearDownloadError }

export const actions = {
  addInstrumentModalOpen() {
    return { type: ActionType.AddInstrumentModalOpened }
  },
  addInstrumentModalClosed() {
    return { type: ActionType.AddInstrumentModalClosed }
  },
  addInstrumentToVisit(visitId: string, instrumentId: string) {
    return { type: ActionType.AddInstrumentToVisit, visitId, instrumentId }
  },
  startAddInstrumentToVisit() {
    return { type: ActionType.AddInstrumentToVisitStarted }
  },
  addInstrumentToVisitFailed(error: string) {
    return { type: ActionType.AddInstrumentToVisitFailed, error }
  },
  AddInstrumentToVisitSucceeded(status: string) {
    return { type: ActionType.AddInstrumentToVisitSucceeded, status }
  },
  clearActionStatus(): Action {
    return { type: ActionType.ClearActionStatus }
  },
  clearDownloadError() {
    return { type: ActionType.ClearDownloadError }
  },
  getList(): Action {
    return { type: ActionType.GetList }
  },
  listRetrieved(visits: Array<IVisit>) {
    return { type: ActionType.ListRetrieved, list: visits }
  },
  loadVisit(id: string): Action {
    return { type: ActionType.LoadVisit, visitId: id }
  },
  loadVisitStarted(): Action {
    return { type: ActionType.LoadVisitStarted }
  },
  loadVisitSuccess(): Action {
    return { type: ActionType.LoadVisitSuccess }
  },
  loadVisitFailure(): Action {
    return { type: ActionType.LoadVisitFailure }
  },
  visitRetrieved(visit: IVisit) {
    return { type: ActionType.VisitRetrieved, visit }
  },
  getEntries(): Action {
    return { type: ActionType.GetEntries }
  },
  entriesRetrieved(entries: Array<IEntry>): Action {
    return { type: ActionType.EntriesRetrieved, entries }
  },
  createVisit(
    patientId: string,
    definitionId: string,
    startTime: string,
    location: string,
  ) {
    return {
      type: ActionType.CreateVisit,
      patientId,
      definitionId,
      startTime,
      location,
    }
  },
  getVisitListForDate(date: string): Action {
    return { type: ActionType.GetVisitListForDate, date }
  },
  getVisitListForDateStarted(): Action {
    return { type: ActionType.GetVisitListForDateStarted }
  },
  getVisitListForDateSuccess(visits: Array<IVisit>): Action {
    return { type: ActionType.GetVisitListForDateSuccess, visits }
  },
  getVisitListForDateFailure(error: string): Action {
    return { type: ActionType.GetVisitListForDateFailure, error }
  },
  visitCreationStarted() {
    return { type: ActionType.VisitCreationStarted }
  },
  visitCreationSucceeded(visit: IVisit) {
    return { type: ActionType.VisitCreationSucceeded, visit }
  },
  visitCreationFailed(error: string) {
    return { type: ActionType.VisitCreationFailed, error }
  },
  updateVisit(id: string, visit: IApiUpdateVisit): Action {
    return { type: ActionType.UpdateVisit, id, visit }
  },
  updateVisitSuccess(visit: IVisit): Action {
    return { type: ActionType.UpdateVisitSuccess, visit }
  },
  updateVisitStatus(visitId: string, status: string) {
    return { type: ActionType.UpdateVisitStatus, visitId, status }
  },
  updateVisitStatusSuccess(visit: IVisit) {
    return { type: ActionType.UpdateVisitStatusSuccess, visit }
  },
  updateVisitStatusFailure(error: string) {
    return { type: ActionType.UpdateVisitStatusFailure, error }
  },
  updateVisitBegin(): Action {
    return { type: ActionType.UpdateVisitBegin }
  },
  updateVisitFailure(error: string): Action {
    return { type: ActionType.UpdateVisitFailure, error }
  },
  updateVisitState(visitId: string, state: IVisitState): Action {
    return { type: ActionType.UpdateVisitState, visitId, state }
  },
  updateVisitStateStarted(): Action {
    return { type: ActionType.UpdateVisitStateStarted }
  },
  updateVisitStateSuccess(visit: IVisit): Action {
    return { type: ActionType.UpdateVisitStateSuccess, visit }
  },
  updateVisitStateFailure(error: string): Action {
    return { type: ActionType.UpdateVisitStateFailure, error }
  },
  reloadVisit(id: string): Action {
    return { type: ActionType.ReloadVisit, id }
  },
  reloadVisitBegin(): Action {
    return { type: ActionType.ReloadVisitBegin }
  },
  reloadVisitSuccess(): Action {
    return { type: ActionType.ReloadVisitSuccess }
  },
  reloadVisitFailure(error: string): Action {
    return { type: ActionType.ReloadVisitFailure, error }
  },
  getVisitDefinition(visitId: string) {
    return { type: ActionType.GetVisitDefinition, visitId }
  },
  startVisitDefinitionRetrieval() {
    return { type: ActionType.GetVisitDefinitionStarted }
  },
  getVisitDefinitionFailed(error: string) {
    return { type: ActionType.GetVisitDefinitionFailed, error }
  },
  getVisitDefinitionSucceeded(definition: IVisitDefinition) {
    return { type: ActionType.GetVisitDefinitionSucceeded, definition }
  },
  getDefinitionList() {
    return { type: ActionType.GetDefinitionList }
  },
  startDefinitionListRetrieval() {
    return { type: ActionType.GetDefinitionListStarted }
  },
  getDefinitionListFailed(error: string) {
    return { type: ActionType.GetDefinitionListFailed, error }
  },
  getDefinitionListSucceeded(definitions: Array<IVisitDefinition>) {
    return { type: ActionType.GetDefinitionListSucceeded, definitions }
  },
  downloadEntryPDF(entryId: string, templateId: string) {
    return { type: ActionType.DownloadEntryPDF, entryId, templateId }
  },
  downloadEntryPDFSuccess() {
    return { type: ActionType.DownloadEntryPDFSuccess }
  },
  downloadEntryPDFFailed(error: string) {
    return { type: ActionType.DownloadEntryPDFFailed, error }
  },
}

export const initialState: State = {
  list: [],
  definitions: undefined,
  currentVisit: {
    visit: undefined,
    definition: undefined,
    entries: undefined,
  },
  isInstrumentModalOpen: false,
}

export function reducer(state: State = initialState, action: AppAction): State {
  switch (action.type) {
    case ActionType.AddInstrumentToVisit:
      return {
        ...state,
        errorMessage: undefined,
      }
    case ActionType.AddInstrumentToVisitSucceeded: {
      return {
        ...state,
      }
    }
    case ActionType.AddInstrumentToVisitFailed:
      return {
        ...state,
        errorMessage: action.error,
      }
    case ActionType.AddInstrumentModalOpened:
      return {
        ...state,
        isInstrumentModalOpen: true,
      }
    case ActionType.AddInstrumentModalClosed:
      return {
        ...state,
        isInstrumentModalOpen: false,
      }
    case ActionType.ClearActionStatus:
      return {
        ...state,
        visitAction: undefined,
      }
    case ActionType.DownloadEntryPDF:
      return {
        ...state,
        downloadReportErrorMessage: undefined,
        isInstrumentPdfLoading: true,
      }
    case ActionType.DownloadEntryPDFSuccess:
      return {
        ...state,
        isInstrumentPdfLoading: false,
      }
    case ActionType.DownloadEntryPDFFailed:
      return {
        ...state,
        downloadReportErrorMessage: action.error,
        isInstrumentPdfLoading: false,
      }
    case ActionType.ClearDownloadError:
      return {
        ...state,
        downloadReportErrorMessage: undefined,
      }
    case ActionType.GetList:
      return {
        ...state,
      }
    case ActionType.ListRetrieved:
      return {
        ...state,
        list: action.list,
      }
    case ActionType.LoadVisit:
      return {
        ...state,
      }
    case ActionType.LoadVisitStarted:
      return {
        ...state,
        loadVisitStatus: ActionStatusId.Pending,
      }
    case ActionType.LoadVisitSuccess:
      return {
        ...state,
        loadVisitStatus: ActionStatusId.Succeeded,
      }
    case ActionType.LoadVisitFailure:
      return {
        ...state,
        loadVisitStatus: ActionStatusId.Failed,
      }
    case ActionType.VisitRetrieved:
      return {
        ...state,
        currentVisit: { visit: action.visit, entries: {} },
      }
    case ActionType.EntriesRetrieved: {
      const entries = {}
      action.entries.forEach((entry) => {
        entries[entry.instrumentId] = entry
      })
      const newState = {
        ...state.currentVisit,
        entries: entries,
      }
      return {
        ...state,
        currentVisit: newState,
      }
    }
    case ActionType.VisitCreationStarted:
      return {
        ...state,
        creationState: ActionStatusId.Pending,
      }
    case ActionType.VisitCreationSucceeded:
      return {
        ...state,
        currentVisit: { visit: action.visit },
        creationState: ActionStatusId.Succeeded,
      }
    case ActionType.VisitCreationFailed:
      return {
        ...state,
        creationState: ActionStatusId.Failed,
        errorMessage: action.error,
      }

    case ActionType.UpdateVisitSuccess:
      return {
        ...state,
        currentVisit: {
          visit: action.visit,
          entries: {},
        },
        visitAction: {
          status: ActionStatusId.Succeeded,
        },
      }
    case ActionType.UpdateVisitBegin:
    case ActionType.UpdateVisitStateStarted:
      return {
        ...state,
        visitAction: {
          status: ActionStatusId.Pending,
        },
      }
    case ActionType.UpdateVisitStateSuccess:
      return {
        ...state,
        visitAction: {
          status: ActionStatusId.Succeeded,
        },
      }
    case ActionType.UpdateVisitFailure:
    case ActionType.UpdateVisitStateFailure:
      return {
        ...state,
        visitAction: {
          status: ActionStatusId.Failed,
        },
      }
    case ActionType.GetVisitDefinitionStarted:
      return {
        ...state,
        errorMessage: undefined,
      }
    case ActionType.GetVisitDefinitionSucceeded: {
      const currentVisit = {
        ...state.currentVisit,
        definition: action.definition,
      }
      return {
        ...state,
        currentVisit: currentVisit,
      }
    }
    case ActionType.GetVisitDefinitionFailed:
      return {
        ...state,
        errorMessage: action.error,
      }
    case ActionType.GetDefinitionListSucceeded:
      return {
        ...state,
        definitions: action.definitions,
      }
    case ActionType.GetDefinitionListFailed:
      return {
        ...state,
        errorMessage: action.error,
      }
    case ActionType.GetVisitListForDateSuccess:
      return {
        ...state,
        list: action.visits,
      }
    case ActionType.GetVisitListForDateFailure:
      return {
        ...state,
        errorMessage: action.error,
      }
    case ActionType.UpdateVisitStatusSuccess:
      return {
        ...state,
        currentVisit: {
          ...state.currentVisit,
          visit: {
            // Previously, this updated the entire visit.  However, the API response appears to omit
            // the patient, causing all of the patient related information to become lost for this
            // visit until it is reloaded.  Reloading is done in a couple ways and somewhat
            // inconsistently so it was decided to scope this update to the status change only,
            // keeping in the spirit of the event's name.
            // PC 2026-01-30
            ...state.currentVisit.visit,
            status: action.visit.status,
          },
        },
        visitAction: {
          status: ActionStatusId.Succeeded,
        },
      }
    default:
      return state
  }
}
